import { useEffect, useState } from 'react';

import type { AuthUser, Lang, UserSettings } from '@lexio/shared';

import { BuildStamp } from './components/BuildStamp';
import { ApiError, flushPending, getSettings, me } from './lib/api';
import { Browse } from './pages/Browse';
import { Login } from './pages/Login';
import { Review } from './pages/Review';
import { SettingsPage } from './pages/SettingsPage';
import { StatsPage } from './pages/StatsPage';
import { Translate } from './pages/Translate';

type Page = 'review' | 'translate' | 'browse' | 'stats' | 'settings';

const NAV: { page: Page; label: string; icon: string }[] = [
  { page: 'review', label: 'Učit', icon: '🎯' },
  { page: 'translate', label: 'Překlad', icon: '🔤' },
  { page: 'browse', label: 'Slovíčka', icon: '📚' },
  { page: 'stats', label: 'Přehled', icon: '📊' },
  { page: 'settings', label: 'Nastavení', icon: '⚙️' },
];

/**
 * Routing pres hash - zadny router jako zavislost. Appka ma ctyri obrazovky,
 * takze `#/browse` v URL bohate staci a funguje i pri obnoveni stranky.
 */
function pageFromHash(): Page {
  const raw = window.location.hash.replace(/^#\/?/, '').split('?')[0];
  return (['review', 'translate', 'browse', 'stats', 'settings'] as const).includes(raw as Page)
    ? (raw as Page)
    : 'review';
}

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState<Page>(pageFromHash);
  const [showLangs, setShowLangs] = useState<Lang[]>([]);

  useEffect(() => {
    const onHash = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Overeni prihlaseni pri startu - token je v httpOnly cookie.
  useEffect(() => {
    void me()
      .then((r) => setUser(r.user))
      .catch((err) => {
        if (!(err instanceof ApiError && err.status === 401)) {
          console.warn('Ověření přihlášení selhalo:', err);
        }
      })
      .finally(() => setChecking(false));
  }, []);

  // Nedoruceny dril z minula odesli hned po prihlaseni.
  useEffect(() => {
    if (user) void flushPending();
  }, [user]);

  // Nastaveni potrebuje uz obrazovka zkouseni (ktere jazyky ukazat vedle sebe).
  useEffect(() => {
    if (!user) return;
    void getSettings()
      .then((r) => setShowLangs(r.settings.showLangs))
      .catch(() => setShowLangs([]));
  }, [user]);

  const applySettings = (s: UserSettings) => setShowLangs(s.showLangs);

  const navigate = (p: string) => {
    window.location.hash = `#/${p}`;
    setPage(p as Page);
  };

  if (checking) {
    return (
      <div className="app">
        <div className="build-bar">
          <BuildStamp />
        </div>
        <div className="center muted">…</div>
      </div>
    );
  }
  if (!user) return <Login onLoggedIn={setUser} />;

  return (
    <div className="app">
      <div className="build-bar">
        <BuildStamp />
      </div>
      <main className="content">
        {page === 'review' && <Review onNavigate={navigate} showLangs={showLangs} />}
        {page === 'translate' && <Translate />}
        {page === 'browse' && <Browse />}
        {page === 'stats' && <StatsPage />}
        {page === 'settings' && (
          <SettingsPage onLoggedOut={() => setUser(null)} onSettingsChanged={applySettings} />
        )}
      </main>

      <nav className="tabbar">
        {NAV.map((n) => (
          <button
            key={n.page}
            className={`tab ${page === n.page ? 'active' : ''}`}
            onClick={() => navigate(n.page)}
          >
            <span className="tab-icon">{n.icon}</span>
            <span className="tab-label">{n.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
